// Sprint 0078 — In-app notification bell.
// Sits in the top bar. Polls /notifications/unread-count every 30s.
// Click → drawer shows latest 50 notifications. Click row → marks read +
// navigates to the linked entity. WhatsApp-channel rows expose an
// "Open WhatsApp" button using the pre-generated wa.me URL.

import { useEffect, useState } from 'react'
import {
  App, Badge, Button, Drawer, Empty, List, Space, Tag, Tooltip, Typography,
} from 'antd'
import {
  BellOutlined,
  CheckOutlined,
  MessageOutlined,
  WhatsAppOutlined,
  MobileOutlined,
} from '@ant-design/icons'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import {
  notificationsApi,
  openWhatsAppShare,
  type NotificationRow,
} from '@/features/notifications/api'
import {
  ensurePushSubscription,
  getPushPermission,
  isPushSupported,
  requestPushPermission,
  sendTestPush,
  unsubscribePush,
} from '@/features/notifications/push'

dayjs.extend(relativeTime)
const { Text } = Typography

// Route map: when a notification has entity_type X, where do we navigate?
const ENTITY_ROUTES: Record<string, (id: string) => string> = {
  maintenance_work_orders: () => '/maintenance/work-orders',
  maintenance_requests: () => '/maintenance/requests',
  purchase_requisitions: () => '/procurement/requisitions',
  purchase_orders: () => '/procurement/purchase-orders',
  grn_headers: () => '/stores/grn',
  purchase_invoices: () => '/maintenance/spare-procurement/invoice',
  // AUTH-001 — approval engine events (REQUESTED/ADVANCED/GRANTED/
  // REJECTED/ESCALATED/CANCELLED) deep-link straight to the request.
  approval_request: (id: string) => `/approvals/my-pending?request_id=${id}`,
}

const SEVERITY_COLOR: Record<string, string> = {
  INFO: 'blue',
  WARNING: 'orange',
  CRITICAL: 'red',
}

export default function NotificationBell() {
  const { message } = App.useApp()
  const [open, setOpen] = useState(false)
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    isPushSupported() ? Notification.permission : 'denied',
  )
  const qc = useQueryClient()
  const navigate = useNavigate()

  // On first load: if permission is already granted, ensure the subscription
  // is synced to the backend. (Idempotent.)
  useEffect(() => {
    if (isPushSupported() && Notification.permission === 'granted') {
      ensurePushSubscription().catch(() => {/* ignore */})
    }
  }, [])

  const onEnablePush = async () => {
    const perm = await requestPushPermission()
    setPushPermission(perm)
    if (perm !== 'granted') {
      message.warning('Notifications blocked. Enable in browser settings to receive alerts.')
      return
    }
    const r = await ensurePushSubscription()
    if (r.ok) {
      message.success('Push notifications enabled on this device')
    } else {
      message.error(`Could not register push: ${r.reason ?? 'unknown'}`)
    }
  }

  const onTestPush = async () => {
    try {
      await sendTestPush()
      message.success('Test push sent. Watch for the notification.')
    } catch {
      message.error('Test push failed')
    }
  }

  const onDisablePush = async () => {
    await unsubscribePush()
    setPushPermission('default')
    message.success('Push disabled on this device')
  }

  // Unread count — polled every 30s when drawer is closed, every 10s when open
  const { data: unread = 0 } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: open ? 10_000 : 30_000,
    refetchIntervalInBackground: true,
  })

  // Notification list — only fetched when drawer is open
  const { data: items = [], refetch: refetchList } = useQuery({
    queryKey: ['notifications-list'],
    queryFn: () => notificationsApi.list({ limit: 50 }),
    enabled: open,
  })

  useEffect(() => {
    if (open) refetchList()
  }, [open, refetchList])

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-list'] })
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-list'] })
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const onRowClick = (n: NotificationRow) => {
    if (!n.read_at) markReadMutation.mutate(n.id)
    // Navigate to the linked entity if we have a route mapping
    if (n.entity_type && ENTITY_ROUTES[n.entity_type]) {
      const route = ENTITY_ROUTES[n.entity_type](n.entity_id ?? '')
      setOpen(false)
      navigate(route)
    }
  }

  const renderRow = (n: NotificationRow) => {
    const isUnread = !n.read_at
    const url = (n.metadata_json as { whatsapp_url?: string } | null)?.whatsapp_url
    return (
      <List.Item
        style={{
          cursor: 'pointer',
          background: isUnread ? '#e6f4ff' : undefined,
          padding: '10px 14px',
        }}
        actions={[
          n.channel === 'WHATSAPP' && url ? (
            <Tooltip title="Open WhatsApp (pre-filled message)" key="wa">
              <Button
                type="link"
                size="small"
                icon={<WhatsAppOutlined style={{ color: '#25D366' }} />}
                onClick={(e) => {
                  e.stopPropagation()
                  openWhatsAppShare(url)
                  if (!n.read_at) markReadMutation.mutate(n.id)
                }}
              >
                Send
              </Button>
            </Tooltip>
          ) : null,
        ].filter(Boolean)}
        onClick={() => onRowClick(n)}
      >
        <List.Item.Meta
          avatar={
            <MessageOutlined
              style={{
                fontSize: 20,
                color:
                  n.severity === 'CRITICAL'
                    ? '#cf1322'
                    : n.severity === 'WARNING'
                      ? '#fa8c16'
                      : '#1677ff',
              }}
            />
          }
          title={
            <Space wrap>
              <Text strong={isUnread}>{n.title}</Text>
              <Tag color={SEVERITY_COLOR[n.severity]} style={{ marginInlineEnd: 0 }}>
                {n.severity}
              </Tag>
              {n.channel === 'WHATSAPP' && (
                <Tag color="green" icon={<WhatsAppOutlined />}>
                  WhatsApp
                </Tag>
              )}
            </Space>
          }
          description={
            <div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{n.body}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(n.created_at).fromNow()}
              </Text>
            </div>
          }
        />
      </List.Item>
    )
  }

  return (
    <>
      <Tooltip title="Notifications">
        <Badge count={unread} offset={[-2, 4]} overflowCount={99}>
          <Button
            type="text"
            shape="circle"
            icon={<BellOutlined style={{ fontSize: 20 }} />}
            onClick={() => setOpen(true)}
          />
        </Badge>
      </Tooltip>
      <Drawer
        title="Notifications"
        placement="right"
        width={420}
        open={open}
        onClose={() => setOpen(false)}
        extra={
          <Space>
            {unread > 0 && (
              <Button
                size="small"
                icon={<CheckOutlined />}
                onClick={() => markAllReadMutation.mutate()}
                loading={markAllReadMutation.isPending}
              >
                Mark all read
              </Button>
            )}
          </Space>
        }
      >
        {/* Push notification controls — top of drawer */}
        <div style={{
          padding: '10px 12px', marginBottom: 8, borderRadius: 8,
          background: pushPermission === 'granted' ? '#f6ffed' : '#fff7e6',
          border: `1px solid ${pushPermission === 'granted' ? '#b7eb8f' : '#ffd591'}`,
        }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space>
              <MobileOutlined />
              <Typography.Text strong>
                {pushPermission === 'granted'
                  ? 'Push notifications enabled'
                  : 'Get instant alerts on this device'}
              </Typography.Text>
            </Space>
            {!isPushSupported() ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Push not supported in this browser. Use Chrome / Edge on Android,
                or add to Home Screen on iOS 16.4+.
              </Typography.Text>
            ) : pushPermission !== 'granted' ? (
              <Space>
                <Button type="primary" size="small" onClick={onEnablePush}>
                  Enable
                </Button>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Allow the browser prompt
                </Typography.Text>
              </Space>
            ) : (
              <Space>
                <Button size="small" onClick={onTestPush}>Send test push</Button>
                <Button size="small" onClick={onDisablePush}>Disable</Button>
              </Space>
            )}
          </Space>
        </div>

        {items.length === 0 ? (
          <Empty description="No notifications yet" />
        ) : (
          <List dataSource={items} renderItem={renderRow} split />
        )}
      </Drawer>
    </>
  )
}
